import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'SearchCache' });

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
  accessCount: number;
  lastAccessed: number;
}

interface SearchPlan {
  q?: string;
  mood?: string;
  domains?: string[];
  content_mix?: Record<string, number>;
  filters?: Record<string, any>;
  geo?: {
    type: 'city' | 'none';
    name?: string;
    radius_km?: number;
  };
  freshness_hours?: number;
  vibe?: string;
  weights?: {
    semantic: number;
    ts?: number;
    vibe?: number;
    freshness?: number;
    resurface?: number;
  };
  explore?: number;
}

class SearchCacheService {
  private cache = new Map<string, CacheEntry>();
  private readonly MAX_CACHE_SIZE = 100;
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly POPULAR_QUERY_TTL = 15 * 60 * 1000; // 15 minutes for popular queries
  
  // Smart cache key generation based on search intent
  private generateCacheKey(plan: SearchPlan): string {
    const normalizedPlan = {
      q: plan.q?.toLowerCase().trim(),
      mood: plan.mood,
      vibe: plan.vibe,
      domains: plan.domains?.sort(),
      geo: plan.geo,
      weights: plan.weights,
      freshness_hours: plan.freshness_hours
    };
    
    return JSON.stringify(normalizedPlan);
  }

  // Intelligent TTL based on query characteristics
  private calculateTTL(plan: SearchPlan): number {
    // Fresh content queries should have shorter cache
    if (plan.freshness_hours && plan.freshness_hours < 24) {
      return 2 * 60 * 1000; // 2 minutes
    }
    
    // Location-based queries cache longer
    if (plan.geo?.type === 'city') {
      return this.POPULAR_QUERY_TTL;
    }
    
    // General mood/vibe queries can cache longer
    if (plan.mood || plan.vibe) {
      return this.POPULAR_QUERY_TTL;
    }
    
    return this.DEFAULT_TTL;
  }

  get(plan: SearchPlan): any | null {
    const key = this.generateCacheKey(plan);
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    const now = Date.now();
    
    // Check if expired
    if (now > entry.timestamp + entry.ttl) {
      this.cache.delete(key);
      log.debug('Cache miss - expired', { key: key.substring(0, 50) });
      return null;
    }
    
    // Update access metrics
    entry.accessCount++;
    entry.lastAccessed = now;
    
    log.debug('Cache hit', { 
      key: key.substring(0, 50),
      accessCount: entry.accessCount,
      age: now - entry.timestamp
    });
    
    return entry.data;
  }

  set(plan: SearchPlan, data: any): void {
    const key = this.generateCacheKey(plan);
    const now = Date.now();
    const ttl = this.calculateTTL(plan);
    
    // Evict least recently used if cache is full
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      this.evictLRU();
    }
    
    this.cache.set(key, {
      data,
      timestamp: now,
      ttl,
      accessCount: 1,
      lastAccessed: now
    });
    
    log.debug('Cache set', { 
      key: key.substring(0, 50),
      ttl: ttl / 1000 + 's',
      size: this.cache.size
    });
  }

  private evictLRU(): void {
    let oldestKey = '';
    let oldestTime = Date.now();
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
      log.debug('Evicted LRU entry', { key: oldestKey.substring(0, 50) });
    }
  }

  // Enhanced query similarity detection
  findSimilarCached(plan: SearchPlan): any | null {
    if (!plan.q) return null;
    
    const queryWords = plan.q.toLowerCase().split(/\s+/);
    
    for (const [cachedKey, entry] of this.cache.entries()) {
      try {
        const cachedPlan = JSON.parse(cachedKey) as SearchPlan;
        
        if (!cachedPlan.q) continue;
        
        // Check if cached query contains most of the new query words
        const cachedWords = cachedPlan.q.toLowerCase().split(/\s+/);
        const matchingWords = queryWords.filter(word => 
          cachedWords.some(cachedWord => 
            cachedWord.includes(word) || word.includes(cachedWord)
          )
        );
        
        // If 70% of words match and contexts are similar
        if (matchingWords.length / queryWords.length >= 0.7 &&
            cachedPlan.mood === plan.mood &&
            cachedPlan.vibe === plan.vibe) {
          
          log.debug('Found similar cached query', {
            original: plan.q,
            cached: cachedPlan.q,
            similarity: matchingWords.length / queryWords.length
          });
          
          return entry.data;
        }
      } catch (e) {
        // Skip invalid cache entries
        continue;
      }
    }
    
    return null;
  }

  clear(): void {
    this.cache.clear();
    log.info('Cache cleared');
  }

  getStats() {
    const entries = Array.from(this.cache.values());
    const now = Date.now();
    
    return {
      size: this.cache.size,
      totalAccesses: entries.reduce((sum, entry) => sum + entry.accessCount, 0),
      averageAge: entries.length > 0 
        ? entries.reduce((sum, entry) => sum + (now - entry.timestamp), 0) / entries.length
        : 0,
      hitRate: entries.length > 0 
        ? entries.filter(entry => entry.accessCount > 1).length / entries.length
        : 0
    };
  }
}

export const searchCache = new SearchCacheService();